import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api-client';
import type { User, LoginCredentials, AuthToken } from '@/types';

interface UseAuthReturn {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<boolean>;
    logout: () => void;
    checkAuth: () => Promise<void>;
}

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

export function useAuth(): UseAuthReturn {
    const [user, setUser] = useState<User | null>(() => {
        // Tentar carregar usuário do localStorage
        const savedUser = localStorage.getItem(AUTH_USER_KEY);
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isAuthenticated = !!user;

    // Verificar autenticação ao iniciar
    const checkAuth = useCallback(async () => {
        setIsLoading(true);
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        if (!token) {
            setUser(null);
            setIsLoading(false);
            return;
        }

        try {
            const response = await api.get<User>('/auth/me');

            if (response.success && response.data) {
                setUser(response.data);
                localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data));
            } else {
                // Token inválido, limpar
                api.clearToken();
                localStorage.removeItem(AUTH_USER_KEY);
                setUser(null);
            }
        } catch {
            // Erro de rede, manter usuário local
            console.warn('Não foi possível verificar autenticação');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fazer login
    const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await api.post<AuthToken>('/auth/login', credentials);

            if (response.success && response.data) {
                const { token, user: loggedUser } = response.data;

                // Salvar token e usuário
                api.setToken(token);
                localStorage.setItem(AUTH_USER_KEY, JSON.stringify(loggedUser));
                setUser(loggedUser);

                return true;
            } else {
                setError(response.error || 'Erro ao fazer login');
                return false;
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erro de conexão';
            setError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fazer logout
    const logout = useCallback(() => {
        api.clearToken();
        localStorage.removeItem(AUTH_USER_KEY);
        setUser(null);
        setError(null);
    }, []);

    // Verificar auth ao montar
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    return {
        user,
        isLoading,
        isAuthenticated,
        error,
        login,
        logout,
        checkAuth,
    };
}

export default useAuth;
